import { z } from 'zod';
import { IsoTimestamp, SchoolId } from './common.js';
/**
 * Fleet-scale legacy CBT migration report (AIMSIS-20930, plan item 1.7).
 *
 * The per-tenant surface (AIMSIS-18812) answers "may THIS tenant convert its
 * legacy academic relationships?". At fleet scale the operator asks something
 * different: for each cbt-web school, how much of the old `eo_*` content has
 * arrived in CBT v2, and which schools still need attention?
 *
 * Four rules shape this contract:
 *
 *   1. A count is reported only when it was MEASURED. A source that could not be
 *      read is `null` with a reason, never a zero — "no questions" and "could not
 *      look" are opposite conclusions and must not share a representation.
 *   2. A dimension the fleet cannot verify is declared, not omitted. Historical
 *      attempts live in the legacy CBT database, which v2's tenant registry no
 *      longer carries, so the report states that instead of implying zero.
 *   3. Two question counts are reported, because they answer different
 *      questions: `questions` is the raw `eo_question` table (what an operator
 *      reconciles against a `SELECT COUNT(*)`), and `questionsInActiveExams` is
 *      the subset the content sync actually writes (the same predicate as
 *      `fetchAimsisExamSnapshot`). Measuring the deficit against the raw table
 *      would report permanent missing rows for every question that belongs to no
 *      active exam.
 *   4. The verdict weighs only what this migration OWNS. Legacy inline media and
 *      historical attempts are reported as separate, explicitly-unfinished
 *      dimensions: converting inline `<img>` into `mediaRefs` and importing
 *      attempts are different jobs, and folding them into the verdict would make
 *      every school permanently `partial` and destroy the verdict's signal.
 */
const count = z.number().int().nonnegative();
/** Rows read from a tenant's legacy `eo_*` tables. */
export const LegacyFleetSourceCounts = z.object({
    /** `eo_question` rows with `COALESCE(deleted, 0) = 0` — the raw bank. */
    questions: count,
    /**
     * The subset reachable from an active exam: `eo_question` joined
     * `eo_question_quiz` joined `eo_quiz` with `status_active = 1`. This is the
     * population `fetchAimsisExamSnapshot` reads and the sync writes, so it is the
     * only defensible denominator for "how many are missing in CBT v2".
     */
    questionsInActiveExams: count,
    /** `eo_question_answers` rows belonging to a live question. */
    questionOptions: count,
    /** `eo_question_quiz` rows with `COALESCE(deleted, 0) = 0`. */
    examQuestions: count,
    /** `eo_quiz` rows with `status_active = 1`. */
    exams: count,
    /** Exam question stems carrying an inline `<img>` reference. */
    stemMediaRefs: count,
    /** Exam answer options carrying an inline `<img>` reference. */
    optionMediaRefs: count,
});
/** Rows the CBT v2 tenant holds today, read from Firestore. */
export const LegacyFleetTargetCounts = z.object({
    /** `schools/{id}/questionBank` documents, all sources. */
    questionBankItems: count,
    /**
     * The subset carrying `source: 'aimsis'` — the only items the content sync can
     * have produced. Measuring the deficit against the TOTAL bank would let
     * CBT-authored questions mask an un-migrated bank, so the deficit uses this.
     */
    aimsisQuestionBankItems: count,
    /** `schools/{id}/quizzes` documents. */
    quizzes: count,
    /** `schools/{id}/media` documents in `ready` status. */
    readyMediaAssets: count,
    /**
     * How many of a bounded sample of ready media assets carry a path under this
     * tenant's own `schools/{id}/` prefix. This is the "served from Firebase
     * Storage" check that can actually be made from Firestore: the media route
     * refuses to sign a URL for a path outside the tenant prefix, so an asset
     * pointing elsewhere is a row nothing can serve.
     */
    mediaPathSample: z.object({
        sampled: count,
        tenantScoped: count,
    }),
});
/**
 * How many schools ONE fleet call may touch.
 *
 * Defined HERE, in the shared contract, and imported by the runner: the request
 * schema bounded `limit` at 50 while the runner's own ceiling was 25, so a caller
 * could ask for 50 schools, have the schema accept it, and be silently clamped to
 * 25 — a documented capability the surface did not have. One constant, one number.
 */
export const LEGACY_FLEET_MAX_PAGE = 25;
/**
 * Legacy inline media, reported as its own unfinished dimension.
 *
 * `legacyRefs` is the number of inline `<img>` references in the source.
 * `storageBackedAssets` is what the tenant already serves from Firebase Storage
 * through API-issued signed URLs. The two are deliberately NOT subtracted from
 * each other: nothing maps an individual legacy `<img src>` to a Storage asset,
 * so any "converted" number would be invented. `pending` is therefore the whole
 * legacy set until a converter exists, and the reason says so.
 */
export const LegacyFleetMediaReport = z.object({
    /**
     * `null` when the legacy source could not be read — NOT 0.
     *
     * A blocked school has an unknown number of refs, and reporting zero let a
     * consumer summing `pendingMedia` across the fleet count an unreadable school as
     * "nothing to convert", which is a factual claim about data never read.
     */
    legacyRefs: count.nullable(),
    /**
     * `null` when the tenant's CBT data could not be read either — NOT 0.
     *
     * The report's own rule is that an unmeasured count is `null`, never zero, and
     * this field broke it: a blocked school reported `0` assets, which reads as
     * "this school serves nothing from Storage" rather than "we could not look".
     */
    storageBackedAssets: count.nullable(),
    conversion: z.object({
        status: z.enum(['notImplemented', 'nothingToConvert', 'unmeasured']),
        /** Legacy refs still awaiting conversion; `null` when unmeasured. */
        pending: count.nullable(),
        reason: z.string().min(1),
    }),
});
/**
 * Historical results are OUT of reach from CBT v2 by design of the registry:
 * `cbt_tenants` carries only `academic_db_name`, and the attempts live in the
 * legacy CBT database (`eo_quiz_user_history`, `eo_quiz_user_answer_history`)
 * which v2 never reads — the old `cbt_list.cbt_db_name` mapping is CBT v1 only.
 * The report says so once per school instead of reporting a zero that would read
 * as "this school sat no exams".
 */
export const LegacyFleetHistoricalResults = z.object({
    status: z.enum(['unreachable', 'notAttempted']),
    reason: z.string().min(1),
});
/**
 * One school's verdict.
 *
 *   complete  — the source was read and every source question reachable from an
 *               active exam has an AIMSIS-sourced counterpart in the bank.
 *   partial   — the source was read and questions are still missing.
 *   blocked   — the source could not be read, so nothing can be claimed.
 */
export const LegacyFleetSchoolVerdict = z.enum(['complete', 'partial', 'blocked']);
export const LegacyFleetSchoolReport = z.object({
    schoolId: SchoolId,
    /** The database the counts were read from. */
    sourceDbName: z.string().min(1),
    verdict: LegacyFleetSchoolVerdict,
    /**
     * The academic year/period the source counts were scoped to — the SAME scope
     * the content sync writes. `null` when the tenant has no active period, which
     * makes the school unmeasurable rather than empty: the sync writes nothing
     * without a context, so a zero would read as "no legacy questions".
     */
    sourceContext: z
        .object({
        schoolYearId: z.number().int(),
        schoolYearName: z.string(),
        schoolPeriodId: z.number().int(),
        schoolPeriodName: z.string(),
    })
        .nullable(),
    /** `null` when the source could not be read — never a zeroed object. */
    source: LegacyFleetSourceCounts.nullable(),
    /**
     * `null` when the tenant's CBT data could not be read either. Nullable for the
     * same reason as `source`: a zeroed object would let a consumer summing
     * `aimsisQuestionBankItems` across the fleet count an unreadable school as zero
     * rather than unknown.
     */
    target: LegacyFleetTargetCounts.nullable(),
    media: LegacyFleetMediaReport,
    historicalResults: LegacyFleetHistoricalResults,
    /**
     * Source questions reachable from an active exam with no AIMSIS-sourced
     * counterpart in the tenant's bank. Never negative: a bank holding more items
     * than the source reports zero missing, not a negative deficit.
     */
    missingQuestions: count,
    /** Why this school is not `complete`, in the operator's words. */
    blockers: z.array(z.string()),
    /** The relationship-migration run id, when this report came from an apply. */
    runId: z.string().nullable(),
    /** Replacement records this run wrote / retired. Zero for a preview. */
    applied: count,
    reverted: count,
    /**
     * The per-school resume point for a relationship migration larger than one
     * page: pass this back as `afterUserId` (with the SAME schoolId) to continue.
     *
     * Reported because the blocker text tells the operator to re-run with
     * `afterUserId`, and a flag without the value leaves them guessing — a wrong
     * guess costs the other schools in the page.
     *
     * This is the NEXT user page's cursor, and it is the resume point ONLY for the
     * `hasMoreUsers` case — when the current page's rows are all written and more
     * users remain beyond it. It is deliberately left `null` whenever
     * `hasMoreRows` is true (rows remain on THIS page): following `nextUserId`
     * there would start the re-run on the NEXT page and silently skip the current
     * page's unwritten rows. The `hasMoreRows` resume instead re-reads the current
     * page from the call's own `afterUserId`/`afterUserIdSchool` (echoed on the
     * report), which is safe because apply is idempotent.
     */
    nextUserId: z.string().nullable(),
    /**
     * Replacement records this run's rollback could NOT retire yet (more than one
     * batch's worth). Dropping this was a defect: a 300-row run would report a
     * successful rollback after retiring 25 rows. A non-zero value forces
     * `partial` and names itself in `blockers`.
     *
     * Its meaning depends on the mode, and the verdict branch that consumes it was
     * written for rollback only — feeding an apply's count into it told the
     * operator to undo the migration they were partway through. On apply this is
     * the migratable rows on the current user page that were not written yet
     * (`migratable.length - convertedRows`), and the apply branch forces `partial`
     * without a separate instruction: the single safe continuation hint (run id
     * plus school cursor) comes from `hasMoreRows`, never "run rollback again".
     */
    remaining: count,
    /**
     * True when this school holds NO run document under the rollback's `runId`, so
     * the rollback wrote nothing for it.
     *
     * Without this the two outcomes are indistinguishable in the report: a school
     * the named run never touched and a school whose rows were genuinely retired
     * both read `reverted: 0`. The first must NOT be reported as a rollback —
     * writing the derived run documents for it stamped every school on the page
     * with this run's terminal state, so an untouched school's admin screen read
     * "rolled back" while its rows stayed active.
     */
    notInRun: z.boolean(),
    /**
     * True when the relationship migration for this school did not finish: either
     * the tenant holds more replacement rows than one rollback batch, or more
     * users remain beyond the apply's page. A large tenant must be advanced
     * explicitly rather than silently stuck at page 1.
     */
    incomplete: z.boolean(),
    /**
     * True when more migratable ROWS remain in this page than one batch writes.
     * Distinct from `incomplete` — a school can be incomplete because of a user
     * cursor without having more rows, and the continuation hint must not tell
     * the operator to advance the user cursor when rows on the current page are
     * still unwritten.
     */
    hasMoreRows: z.boolean(),
});
export const LegacyFleetTotals = z.object({
    schools: count,
    complete: count,
    partial: count,
    blocked: count,
    /** Summed over schools whose source was readable. */
    sourceQuestions: count,
    missingQuestions: count,
    /** Legacy inline media refs still awaiting conversion, fleet-wide. */
    pendingMedia: count,
    applied: count,
    reverted: count,
    /**
     * Schools in this page that hold no APPLY run document under the rollback's `runId`
     * (none exists, a different kind, or already rolled back).
     *
     * Counted so a fleet rollback's report cannot read as "24 schools rolled back"
     * when only 8 were: the other 16 were never part of the run, and reporting them
     * as reverted-by-zero is the silent overclaim this counter exists to prevent.
     */
    notInRun: count,
});
export const LegacyFleetMigrationMode = z.enum(['preview', 'apply', 'rollback']);
export const LegacyFleetMigrationReport = z.object({
    mode: LegacyFleetMigrationMode,
    generatedAt: IsoTimestamp,
    /** The relationship-migration effective date this run used, when it applied. */
    effectiveFrom: z.string().nullable(),
    schools: z.array(LegacyFleetSchoolReport),
    totals: LegacyFleetTotals,
    /**
     * Resume point: the last school this call examined, when the fleet is larger
     * than one page. `null` means the whole fleet was covered. Without it an
     * operator cannot tell a complete sweep from a truncated one.
     */
    nextCursor: z.string().nullable(),
    /**
     * The fleet cursor this call was made with, echoed back so a per-school
     * continuation hint can REPRODUCE the page the school is on.
     *
     * `afterUserIdSchool` is refused when the named school is not in the page, so a
     * hint that named only the school and its user cursor threw for every
     * unfinished school that was not on the first page. `null` means the call
     * started at the beginning of the fleet, which is what the hint then omits.
     */
    afterSchoolId: z.string().nullable(),
    /**
     * The user-level resume point THIS call was made with, echoed back from the
     * request so a `hasMoreRows` continuation hint can RE-READ the current user
     * page.
     *
     * The report's `nextUserId` names the NEXT user page, which must never be
     * followed while rows remain on the current page. Draining the page means
     * re-running from THIS call's own `afterUserId`/`afterUserIdSchool` — safe
     * because apply is idempotent. `null` when the call examined the school from
     * its first user (no user cursor was supplied), which is what the hint then
     * omits. See the runner's `applySchoolRelationships` and the CLI/HTTP hints.
     */
    afterUserId: z.string().nullable(),
    afterUserIdSchool: z.string().nullable(),
    /**
     * Fleet-wide caveats — the dimensions this report cannot verify for ANY
     * school. Surfaced at the top level so a per-school `complete` is never read
     * as "the whole migration is finished".
     */
    warnings: z.array(z.string()),
});
/**
 * The reserved id the per-tenant migration uses for its preview singleton
 * (AIMSIS-18812). A caller must never be able to stamp replacement rows with it:
 * those rows would sit outside the per-tenant rollback path.
 */
export const LEGACY_FLEET_RESERVED_RUN_ID = 'preview';
/** A run id that may be used to stamp replacement rows. */
const FleetRunId = z
    .string()
    .min(1)
    .max(128)
    .refine((value) => value !== LEGACY_FLEET_RESERVED_RUN_ID, {
    message: `"${LEGACY_FLEET_RESERVED_RUN_ID}" is reserved for the tenant preview record.`,
});
/**
 * Request body for the fleet apply / rollback.
 *
 * Bounded on purpose: `limit` caps how many schools ONE call touches, and
 * `afterSchoolId` resumes where the previous call stopped. A single request that
 * writes every tenant in the fleet is an unbounded operation that cannot be
 * retried or partially reverted, so the fleet surface pages like every other
 * write path in this codebase.
 */
export const LegacyFleetMigrationRequest = z
    .object({
    /** Schools examined per call. Clamped to [1, MAX]. */
    limit: z.number().int().min(1).max(LEGACY_FLEET_MAX_PAGE).optional(),
    /** Resume point — only schools sorting after this id are examined. */
    afterSchoolId: z.string().min(1).max(64).optional(),
    /**
     * Resume point WITHIN one school's relationship migration, for a tenant whose
     * user count exceeds one page (2000).
     *
     * ALWAYS paired with `afterUserIdSchool`. A bare cursor is refused rather than
     * applied to every school in the page: synced user ids look like `aimsis:12`,
     * which sort BEFORE a cursor like `user-2000`, so the other schools would read
     * zero users, write no rows, and be reported finished.
     */
    afterUserId: z.string().min(1).max(128).optional(),
    /** The school `afterUserId` applies to. Required whenever `afterUserId` is set. */
    afterUserIdSchool: z.string().min(1).max(64).optional(),
    /** Relationship-migration effective date; defaults to today in Asia/Jakarta. */
    effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    /**
     * The apply operation's id. Every call of one operation shares it, so the
     * whole sweep has ONE identity and a rollback can retire every batch. A
     * per-call id would leave earlier batches active behind a rollback that
     * reported success.
     */
    runId: FleetRunId.optional(),
    /**
     * Also run the AIMSIS content sync (roster, classrooms, question bank, exams)
     * for each school in the page before the relationship apply. Off by default:
     * the sync is a heavier, independently-recoverable operation, and an operator
     * verifying migration completeness usually does not want to re-sync rosters.
     */
    syncContent: z.boolean().optional(),
})
    // The SAME pairing the preview query enforces. The runner refuses a bare cursor
    // too, but it refuses by THROWING, and the route maps a throw to a 500 — so a
    // bare cursor on /apply answered 500 for a request the runbook explicitly tells
    // the operator to make, and the route test pinned that 500 as the contract. A
    // malformed request is refused by the schema, on every endpoint.
    .refine((value) => !value.afterUserId || Boolean(value.afterUserIdSchool), {
    message: 'afterUserId must name its school (afterUserIdSchool)',
    path: ['afterUserIdSchool'],
});
/**
 * Query parameters for the read-only preview. Deliberately a SEPARATE schema from
 * the apply body: validating a GET against the apply contract would silently
 * accept and drop `runId`/`syncContent`, documenting a capability the route does
 * not have.
 */
export const LegacyFleetMigrationQuery = z
    .object({
    limit: z.number().int().min(1).max(LEGACY_FLEET_MAX_PAGE).optional(),
    afterSchoolId: z.string().min(1).max(64).optional(),
    afterUserId: z.string().min(1).max(128).optional(),
    afterUserIdSchool: z.string().min(1).max(64).optional(),
    effectiveFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
})
    // The runner ALSO refuses a bare cursor, but it does so by THROWING, and the
    // route maps a throw to a 500. The schema is where a malformed request is
    // refused, so the pairing is enforced here too: a documented `?afterUserId=`
    // that answers 500 is a contract the surface does not honour.
    .refine((value) => !value.afterUserId || Boolean(value.afterUserIdSchool), {
    message: 'afterUserId must name its school (afterUserIdSchool)',
    path: ['afterUserIdSchool'],
});
export const LegacyFleetRollbackRequest = z.object({
    /** The apply operation to retire. Required — there is no "roll back everything". */
    runId: FleetRunId,
    limit: z.number().int().min(1).max(LEGACY_FLEET_MAX_PAGE).optional(),
    afterSchoolId: z.string().min(1).max(64).optional(),
});
//# sourceMappingURL=legacy-fleet-migration.js.map